"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api/client";

// ─── Types ────────────────────────────────────────────────────────────────────

export type DiscussionSort = "new" | "top";

export type ThreadAuthor = {
  name: string;
  avatar_url: string | null;
  is_admin: boolean;
  post_count: number;
  joined_at: string | null;
};

export type GuideImage = {
  path: string;
  public_url: string;
};

export type GuideThread = {
  id: string;
  guide_id: string;
  user_id: string;
  title: string;
  section: string;
  body: string;
  is_pinned: boolean;
  is_locked: boolean;
  vote_count: number;
  reply_count: number;
  image_paths: string[] | null;
  image_urls: string[];
  created_at: string;
  updated_at: string;
  author: ThreadAuthor;
  user_vote: -1 | 0 | 1;
};

export type GuidePost = {
  id: string;
  thread_id: string;
  user_id: string;
  body: string;
  is_solution: boolean;
  vote_count: number;
  image_paths: string[] | null;
  image_urls: string[];
  created_at: string;
  updated_at: string;
  author: ThreadAuthor;
  user_vote: -1 | 0 | 1;
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useGuideThreads(
  guideId: string,
  options: { section?: string; sort?: DiscussionSort } = {},
) {
  const { section = "all", sort = "new" } = options;
  return useQuery({
    queryKey: ["guide-threads", guideId, section, sort],
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: () => {
      const params = new URLSearchParams({ section, sort });
      return apiFetch<{ threads: GuideThread[]; next_cursor: string | null }>(
        `/api/guides/${guideId}/threads?${params}`,
      );
    },
  });
}

export function useGuideThread(guideId: string, threadId: string | null) {
  return useQuery({
    queryKey: ["guide-thread", guideId, threadId],
    enabled: !!threadId,
    retry: false,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    queryFn: () =>
      apiFetch<{ thread: GuideThread; posts: GuidePost[] }>(
        `/api/guides/${guideId}/threads/${threadId}`,
      ),
  });
}

export function useCreateThread(guideId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      section: string;
      body: string;
      image_paths?: string[];
    }) =>
      apiFetch<{ thread: GuideThread }>(`/api/guides/${guideId}/threads`, {
        method: "POST",
        body: JSON.stringify(input),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guide-threads", guideId] });
    },
  });
}

export function useCreatePost(guideId: string, threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; image_paths?: string[] }) =>
      apiFetch<{ post: GuidePost }>(
        `/api/guides/${guideId}/threads/${threadId}/posts`,
        {
          method: "POST",
          body: JSON.stringify(input),
          headers: { "Content-Type": "application/json" },
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guide-thread", guideId, threadId] });
      queryClient.invalidateQueries({ queryKey: ["guide-threads", guideId] });
    },
  });
}

export function useDeleteThread(guideId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) =>
      apiFetch<{ ok: true }>(`/api/guides/${guideId}/threads/${threadId}`, {
        method: "DELETE",
      }),
    onSuccess: (_data, threadId) => {
      queryClient.invalidateQueries({ queryKey: ["guide-threads", guideId] });
      queryClient.removeQueries({ queryKey: ["guide-thread", guideId, threadId] });
    },
  });
}

export function useDeletePost(guideId: string, threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) =>
      apiFetch<{ ok: true }>(
        `/api/guides/${guideId}/threads/${threadId}/posts/${postId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guide-thread", guideId, threadId] });
      queryClient.invalidateQueries({ queryKey: ["guide-threads", guideId] });
    },
  });
}

export function useUploadGuideImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const response = await fetch("/api/guides/upload-image", {
        method: "POST",
        body: form,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          (data as { message?: string })?.message ?? `Upload failed (${response.status})`,
        );
      }
      return (await response.json()) as GuideImage;
    },
  });
}

export function useGuideVote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      target_type: "thread" | "post";
      target_id: string;
      value: -1 | 0 | 1;
    }) =>
      apiFetch<{ vote_count: number; user_vote: number }>("/api/guides/vote", {
        method: "POST",
        body: JSON.stringify(input),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guide-threads"] });
      queryClient.invalidateQueries({ queryKey: ["guide-thread"] });
    },
  });
}
